import { LIST_ACTION_CONCURRENCY } from "containers/Resources/listActionConcurrency";

export type RunWithConcurrencyOptions = {
    concurrency?: number;
    onItemComplete?: (completed: number, total: number) => void;
};

/**
 * Run an async worker over items with bounded parallelism.
 * Results preserve input order. Workers should catch errors internally when
 * partial failure is acceptable; otherwise the first thrown error aborts the pool.
 */
export async function runWithConcurrency<T, R>(
    items: T[],
    worker: (item: T, index: number) => Promise<R>,
    options: RunWithConcurrencyOptions = {}
): Promise<R[]> {
    const { concurrency = LIST_ACTION_CONCURRENCY, onItemComplete } = options;

    if (items.length === 0) {
        return [];
    }

    const results: R[] = new Array(items.length);
    let nextIndex = 0;
    let completed = 0;
    let aborted = false;
    let failure: unknown;
    const workerCount = Math.min(concurrency, items.length);

    const runWorker = async () => {
        while (!aborted) {
            const index = nextIndex;
            if (index >= items.length) {
                return;
            }
            nextIndex += 1;
            try {
                results[index] = await worker(items[index], index);
            } catch (error) {
                aborted = true;
                failure = error;
                return;
            }
            completed += 1;
            if (onItemComplete) {
                onItemComplete(completed, items.length);
            }
        }
    };

    await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

    if (failure !== undefined) {
        throw failure;
    }

    return results;
}
